/**
 * Canonicalize a side/page/decoration-location name to a stable token so differently-formatted
 * representations of the same physical side line up — e.g. Design Huddle's own page name
 * ("sleeve_left") with this app's built side name ("left_sleeve"), or a DB SideName ("Left Sleeve")
 * with an in-memory key ("leftsleeve").
 *
 * WI 1819: only treat a name as a sleeve when it is actually sleeve/shoulder-specific ("left
 * sleeve", "sleeveleft", "shoulder left", ...). This used to be a bare `includes('left')` /
 * `includes('right')` check (in product-color-selection.component.ts), which also matched any
 * CUSTOM decoration-location name that merely contains those words but isn't a sleeve at all —
 * e.g. "Left Logo" or "Left Chest" (real, admin-entered Print Location names used for embroidery
 * placements). Such a name collapsed to 'sleeve_left', and buildAvailableSides() then gated it
 * behind the product having a real ImageFilePath_SleeveLeft/Right photo — a logo/chest placement
 * never has one, so the side was silently dropped from availableSidesMap. Downstream, that could
 * leave the default/locked style's Sides[] empty in the SaveHuddleProductStyles payload: the
 * backend then never assigns a CustomizedProductId, and the whole publish is cancelled with an
 * opaque "save completed without creating a product record" error.
 *
 * Bare "left"/"right" (no "sleeve"/"shoulder" qualifier) is intentionally NOT treated as a
 * sleeve either, consistent with WI 1817's equivalent fix in decoration-location.constants.ts
 * (those bare aliases belong to cap/hat sides, not sleeves).
 *
 * Pure function, no Angular — unit-tested in canonical-side.util.spec.ts.
 *
 * Returns: 'front' | 'back' | 'sleeve_left' | 'sleeve_right' | (collapsed lowercase input).
 */
export function canonicalSide(name: string | null | undefined): string {
  const n = (name || '').toLowerCase().replace(/[\s_-]+/g, '');
  if (n.includes('front')) { return 'front'; }
  if (n.includes('back')) { return 'back'; }
  const isSleeveName = n.includes('sleeve') || n.includes('shoulder');
  if (isSleeveName && n.includes('left')) { return 'sleeve_left'; }
  if (isSleeveName && n.includes('right')) { return 'sleeve_right'; }
  return n;
}
